import { useCallback, useMemo, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { portal } from './portal';

/**
 * Criminal-background translation pipeline — Hub-side (Hub PR #1797).
 *
 * The Hub detects language and produces the English translation SERVER-SIDE,
 * triggered by the portal's step submit (the pipeline watches for step fields
 * with metadata.documentType === "police-clearance"; uploading without
 * submitting does nothing). The portal's whole job is:
 *   1. poll GET /applications/:id/criminal-background/status after submit,
 *   2. feed the results into the form engine's existing translation UI
 *      (badge + "Download English Translation PDF" notice), and
 *   3. surface the translated artifact on the Documents page.
 *
 * Downloads go through the BFF: /api/v1/document-packages/artifacts/:id/download.
 */

export type CbcProcessingStatus =
  | 'not_started'
  | 'extracting'
  | 'detecting_language'
  | 'translating'
  | 'generating_pdf'
  | 'completed'
  | 'failed'
  | 'manual_intervention_required'
  // Unsupported-language fallback (Asana 1217709826504784): the Hub detected
  // a language it will not attempt AI translation for and fenced the record
  // here BEFORE any translation attempt. Terminal-for-the-pipeline — the
  // applicant must upload their own English version via
  // uploadCbcApplicantTranslation, or it falls to the compliance queue.
  | 'awaiting_applicant_translation';

export interface CbcArtifact {
  artifactId: string;
  artifactRole: 'original' | 'ai_translation' | 'manual_translation' | 'certified_translation';
  languageCode: string;
  mimeType: string;
  fileName: string | null;
  fileSizeBytes: number;
  versionNumber: number;
  generatedBy: string;
  createdAt: string;
  /** Hub-shaped path — rewrite with {@link artifactDownloadUrl} before use. */
  downloadUrl: string;
}

export interface CbcPackage {
  requirementId: string;
  stepKey: string;
  fieldKey: string;
  status: string;
  record: {
    recordId: string;
    processingStatus: CbcProcessingStatus;
    reviewStatus: string;
    detectedLanguage: string | null;
    translationRequired: boolean | null;
    versionNumber: number;
    updatedAt: string;
    original: CbcArtifact | null;
    translation: CbcArtifact | null;
  } | null;
}

const TERMINAL: ReadonlySet<string> = new Set([
  'completed',
  'failed',
  'manual_intervention_required',
  'awaiting_applicant_translation',
]);

/** Nexus-governed failure copy. Applicant uploads are retained; manual or
 * certified translation is a staff action, never an applicant requirement. */
export const CBC_TRANSLATION_REVIEW_NOTICE =
  'Your document was received, but the English translation could not be completed automatically. ' +
  'Our team will review it.';
/** Post-submit window during which we poll even with no (or only terminal)
 *  packages — the Hub creates/bumps the record asynchronously after submit. */
const POST_SUBMIT_POLL_MS = 5 * 60 * 1000;
const POLL_INTERVAL_MS = 4000;

/** Map a Hub artifact downloadUrl (or artifactId) to the BFF proxy route. */
export function artifactDownloadUrl(artifact: Pick<CbcArtifact, 'artifactId'> | null | undefined): string | null {
  if (!artifact?.artifactId) return null;
  return `/api/v1/document-packages/artifacts/${encodeURIComponent(artifact.artifactId)}/download`;
}

/** "es" → "Spanish" (falls back to the raw code). Hub sends ISO 639-1. */
export function languageDisplayName(code: string | null | undefined): string {
  if (!code) return 'Non-English';
  try {
    const name = new Intl.DisplayNames(['en'], { type: 'language' }).of(code);
    return name && name !== code ? name : code;
  } catch {
    return code;
  }
}

const isEnglishCode = (code: string | null | undefined) => !!code && /^en/i.test(code);

/**
 * Poll the pipeline status for an application. Polling runs while any package
 * is non-terminal, or during the 5-minute window after `markSubmitted()`
 * (call it after every successful step submit — it's free for non-CBC steps,
 * where the status list simply never grows). Otherwise it's a single fetch.
 */
export function useCbcStatus(appId: string | number | undefined) {
  const submittedAtRef = useRef(0);
  const queryClient = useQueryClient();
  const q = useQuery({
    queryKey: ['cbcStatus', String(appId)],
    queryFn: async () => (await portal.criminalBackgroundStatus(appId!)) as { packages?: CbcPackage[] },
    enabled: appId != null && appId !== '',
    staleTime: 0,
    refetchInterval: (query) => {
      const pkgs = (query.state.data?.packages ?? []) as CbcPackage[];
      const active = pkgs.some((p) => p.record && !TERMINAL.has(p.record.processingStatus));
      const inWindow = Date.now() - submittedAtRef.current < POST_SUBMIT_POLL_MS;
      return active || inWindow ? POLL_INTERVAL_MS : false;
    },
    refetchIntervalInBackground: false,
  });
  const markSubmitted = useCallback(() => {
    submittedAtRef.current = Date.now();
    void queryClient.invalidateQueries({ queryKey: ['cbcStatus', String(appId)] });
  }, [appId, queryClient]);
  const refetch = q.refetch;
  const refetchPackages = useCallback(async (): Promise<CbcPackage[]> => {
    const result = await refetch();
    if (result.isError) throw result.error;
    return (result.data?.packages ?? []) as CbcPackage[];
  }, [refetch]);
  return useMemo(
    () => ({
      packages: (q.data?.packages ?? []) as CbcPackage[],
      markSubmitted,
      isReady: q.isSuccess,
      refetchPackages,
    }),
    [q.data, q.isSuccess, markSubmitted, refetchPackages],
  );
}

/** Hub-derived language state, shaped for the engine's AP context. */
export interface HubLanguageState {
  /** Per-field analysis results (badge: "English Document" / "X Detected"). */
  results: Record<string, { isEnglish: boolean; detectedLanguage: string }>;
  statusByField: Record<string, string>;
  analysisStatus: Record<string, string>;
  /** translationDataFromDB for the engine — matched to the field's stored
   *  file URL (the engine compares pathnames). */
  translation: Record<string, unknown> | null;
  /** Terminal processing failures are rendered by the portal with governed
   * applicant-safe copy. They must not enter the engine's generic error state,
   * which asks the applicant to upload a manual translation. */
  reviewRequiredByField: Record<string, string>;
  /** Unsupported-language fallback (Asana 1217709826504784): the Hub will not
   * attempt AI translation for this field's detected language. The portal
   * must offer the applicant its own upload door rather than waiting on a
   * translation that was never attempted. */
  translationFallbackByField: Record<string, { requirementId: string; detectedLanguage: string }>;
}

/** The CURRENT first file of a form field: its URL and (if known) file name. */
export interface FieldFileRef {
  url?: string;
  fileName?: string;
}

/**
 * Project the Hub's packages for one step into the engine's language state.
 * `getFieldFile` must return the CURRENT form value's first file for a field
 * (the engine matches translationDataFromDB.originalDocumentUrl against the
 * field value's first file URL by pathname).
 *
 * Attachment guard: language and translation state belongs to a currently
 * attached file. If the field is empty, project nothing from its old package.
 *
 * Staleness guard: a pipeline record describes the file that was SUBMITTED —
 * if the applicant has since replaced the field's file, the old record (and
 * its translation) must not be presented as belonging to the new file. The
 * status payload shares no document id with the form value, so the match key
 * is the original artifact's fileName vs the field file's name; when both
 * names are known and differ, the package is suppressed. A missing name on
 * either side stays permissive (some refs carry no name — better to show the
 * pipeline result than to hide it on an unknowable comparison).
 */
export function deriveHubLanguageState(
  packages: CbcPackage[],
  stepKey: string,
  getFieldFile: (fieldKey: string) => FieldFileRef | undefined,
): HubLanguageState {
  const results: HubLanguageState['results'] = {};
  const statusByField: Record<string, string> = {};
  const analysisStatus: Record<string, string> = {};
  const reviewRequiredByField: Record<string, string> = {};
  const translationFallbackByField: HubLanguageState['translationFallbackByField'] = {};
  let translation: Record<string, unknown> | null = null;

  for (const pkg of packages) {
    if (pkg.stepKey !== stepKey || !pkg.record) continue;
    const rec = pkg.record;
    const field = getFieldFile(pkg.fieldKey);
    if (!field) continue; // removed file: clear all document-specific guidance
    const recordName = rec.original?.fileName;
    const stale = !!recordName && !!field?.fileName && recordName !== field.fileName;
    if (stale) continue; // record is about a since-replaced file
    if (rec.detectedLanguage) {
      // `translationRequired` is tri-state: true / false / null (pipeline has
      // not decided yet — e.g. manual_intervention_required). Only an explicit
      // `false` or an English language code means "no translation needed";
      // null must NOT be read as English.
      const english = isEnglishCode(rec.detectedLanguage) || rec.translationRequired === false;
      results[pkg.fieldKey] = { isEnglish: english, detectedLanguage: languageDisplayName(rec.detectedLanguage) };
      statusByField[pkg.fieldKey] = english ? 'english' : 'non-english';
      analysisStatus[pkg.fieldKey] = 'completed';
    }
    // One translation object (engine contract holds a single active one);
    // prefer the package that actually required translation. Without the
    // field's current URL the engine can never match the notice to a file —
    // emit nothing rather than an unanchored translation object.
    const needsTranslation =
      rec.translationRequired === true ||
      (rec.translationRequired == null && !!rec.detectedLanguage && !isEnglishCode(rec.detectedLanguage));
    const needsStaffReview =
      rec.processingStatus === 'failed' ||
      rec.processingStatus === 'manual_intervention_required';
    const needsApplicantFallback = rec.processingStatus === 'awaiting_applicant_translation';
    if (needsStaffReview && field.url) {
      reviewRequiredByField[pkg.fieldKey] = CBC_TRANSLATION_REVIEW_NOTICE;
    }
    if (needsApplicantFallback && field.url) {
      translationFallbackByField[pkg.fieldKey] = {
        requirementId: pkg.requirementId,
        detectedLanguage: languageDisplayName(rec.detectedLanguage),
      };
    }
    if (!translation && needsTranslation && field.url && !needsStaffReview && !needsApplicantFallback) {
      const base = {
        fieldName: pkg.fieldKey,
        detectedLanguage: languageDisplayName(rec.detectedLanguage),
        originalDocumentUrl: field.url,
      };
      if (rec.processingStatus === 'completed' && rec.translation) {
        translation = {
          ...base,
          status: 'completed',
          translatedDocumentUrl: artifactDownloadUrl(rec.translation),
        };
      } else {
        translation = { ...base, status: 'in_progress' };
      }
    }
  }
  return {
    results,
    statusByField,
    analysisStatus,
    translation,
    reviewRequiredByField,
    translationFallbackByField,
  };
}
